import React, { useEffect, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { Pagination } from '@mui/material';
import { GET_PROPERTY_PERFORMANCE, GET_PROPERTY_PERFORMANCE_SUMMARY } from '../graphql/propertyPerformance';
import { GET_USERS } from '../graphql/queries';
import { UserRole } from '../utils/enums';
import { getPropertyManagerDisplayName, getPropertyManagerIdsFromProperty, parseManagementFilter } from '../utils/propertyManagers';
import PropertyManagementFilter from '../components/properties/PropertyManagementFilter';

const PAGE_SIZE = 40;
const AREA_GROUPS = {
  KIAWAH_SEABROOK: {
    label: 'Kiawah + Seabrook',
    areas: ['KIAWAH', 'SEABROOK_ISLAND'],
  },
  IOP_WILD_DUNES: {
    label: 'Isle of Palms + Wild Dunes',
    areas: ['ISLE_OF_PALMS', 'ISLE_OF_PALMS_WILD_DUNES'],
  },
};

const scoreClasses = (score) => {
  if (score >= 85) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (score >= 70) return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-red-100 text-red-800 border-red-200';
};

export default function PropertyPerformance() {
  const apolloClient = useApolloClient();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [areaGroup, setAreaGroup] = useState('');
  const [propertyManagerId, setPropertyManagerId] = useState('');
  const [sort, setSort] = useState('ALPHABETICAL');
  const [streamlineOnly, setStreamlineOnly] = useState(true);
  const [summaries, setSummaries] = useState({});
  const [summaryLoading, setSummaryLoading] = useState({});
  const [summaryErrors, setSummaryErrors] = useState({});
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);
  const { data: usersData } = useQuery(GET_USERS, { fetchPolicy: 'cache-first' });
  const propertyManagers = (usersData?.users || [])
    .filter(user => user.role === UserRole.PROPERTY_MANAGER && user.isActive !== false)
    .sort((left, right) => {
      const leftName = left.name || `${left.firstName || ''} ${left.lastName || ''}`.trim() || left.email;
      const rightName = right.name || `${right.firstName || ''} ${right.lastName || ''}`.trim() || right.email;
      return leftName.localeCompare(rightName);
    });
  const managerById = new Map((usersData?.users || []).map(manager => [String(manager.id), manager]));
  const { data, loading, error, refetch } = useQuery(GET_PROPERTY_PERFORMANCE, {
    variables: {
      input: {
        page,
        limit: PAGE_SIZE,
        search: search.trim() || undefined,
        areas: areaGroup ? AREA_GROUPS[areaGroup].areas : undefined,
        ...parseManagementFilter(propertyManagerId),
        streamlineOnly,
        sort,
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const response = data?.propertyPerformance;
  const items = response?.items || [];
  const pages = Math.max(1, Math.ceil((response?.total || 0) / PAGE_SIZE));

  const loadSummary = async (propertyId) => {
    setSummaryLoading(current => ({ ...current, [propertyId]: true }));
    setSummaryErrors(current => ({ ...current, [propertyId]: null }));
    try {
      const result = await apolloClient.query({
        query: GET_PROPERTY_PERFORMANCE_SUMMARY,
        variables: { propertyId },
        fetchPolicy: 'network-only',
      });
      setSummaries(current => ({
        ...current,
        [propertyId]: result.data.propertyPerformanceSummary,
      }));
      await refetch();
    } catch (summaryError) {
      setSummaryErrors(current => ({
        ...current,
        [propertyId]: summaryError.message || 'Unable to calculate score',
      }));
    } finally {
      setSummaryLoading(current => ({ ...current, [propertyId]: false }));
    }
  };

  return (
    <div className="mx-auto max-w-7xl py-4">
      <div className="mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white lg:text-gray-900">Property Health &amp; Performance</h1>
          <p className="mt-1 text-sm text-gray-300 lg:text-gray-600">Properties load quickly. Calculate a score only for the property you want to review.</p>
        </div>
      </div>

      <div className="mb-4 rounded-lg bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="block xl:col-span-1">
            <span className="mb-1 block text-sm font-medium text-gray-700">Search properties</span>
            <input
              id="performance-search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Name, address, or code"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Area</span>
            <select value={areaGroup} onChange={(event) => { setAreaGroup(event.target.value); setPage(1); }} className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
              <option value="">All areas</option>
              {Object.entries(AREA_GROUPS).map(([value, group]) => <option key={value} value={value}>{group.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Property manager</span>
            <PropertyManagementFilter
              value={propertyManagerId}
              onChange={(value) => { setPropertyManagerId(value); setPage(1); }}
              users={propertyManagers}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Sort by</span>
            <select value={sort} onChange={(event) => { setSort(event.target.value); setPage(1); }} className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
              <option value="ALPHABETICAL">Alphabetical</option>
              <option value="SCORE_ASC">Lowest to highest score</option>
              <option value="SCORE_DESC">Highest to lowest score</option>
              <option value="BEDS_BATHS_DESC">Beds/Baths (highest first)</option>
            </select>
          </label>
        </div>
        <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
          <input
            id="streamline-properties-only"
            type="checkbox"
            checked={streamlineOnly}
            onChange={(event) => {
              setStreamlineOnly(event.target.checked);
              setPage(1);
            }}
            className="peer sr-only"
          />
          <span
            aria-hidden="true"
            className="relative mt-0.5 h-6 w-11 flex-none rounded-full bg-gray-300 transition-colors peer-checked:bg-sky-600 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-sky-600 after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow-sm after:transition-transform peer-checked:after:translate-x-5"
          />
          <span>
            <span className="block text-sm font-medium text-gray-800">Streamline properties only</span>
            <span className="mt-0.5 block text-xs text-gray-500">Show vacation rental properties connected to Streamline. Turn this off to include other active properties.</span>
          </span>
        </label>
      </div>

      {loading && !response ? <div className="rounded-lg bg-white p-8 text-center text-gray-600">Loading property performance…</div> : null}
      {error ? <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">Unable to load property performance: {error.message}</div> : null}

      {!error && response && (
        <div className="overflow-hidden rounded-lg bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Property', 'Health', 'Active work', 'Urgent', 'Closure review', 'Completed (120d)', 'Reviews (180d)', 'Details'].map(label => (
                    <th key={label} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 ${label === 'Details' ? 'text-right' : 'text-left'}`}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map(item => {
                  const summary = summaries[item.propertyId] || (item.healthScore != null ? item : null);
                  const isCalculating = Boolean(summaryLoading[item.propertyId]);
                  const summaryError = summaryErrors[item.propertyId];
                  return (
                  <tr key={item.propertyId} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900">{item.propertyName}</div>
                      <div className="mt-1 text-xs text-gray-500">{item.address || item.area || '—'}</div>
                      {(item.beds != null || item.baths != null) && <div className="mt-1 text-xs text-gray-500">{item.beds ?? '—'} beds · {item.baths ?? '—'} baths</div>}
                      {(() => {
                        const managerNames = getPropertyManagerIdsFromProperty(item)
                          .map((id) => managerById.get(String(id)))
                          .filter(Boolean)
                          .map(getPropertyManagerDisplayName)
                          .filter(Boolean);
                        if (managerNames.length === 0) return null;
                        return (
                          <div className="mt-1 text-xs text-gray-400">
                            Manager{managerNames.length === 1 ? '' : 's'}: {managerNames.join(', ')}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-4">
                      {summary ? (
                        <div>
                          <span className={`inline-flex min-w-14 justify-center rounded-full border px-3 py-1 text-sm font-bold ${scoreClasses(summary.healthScore)}`}>{summary.healthScore}</span>
                          <div className="mt-1 whitespace-nowrap text-xs text-gray-500">Maintenance {summary.maintenanceScore} · Reviews {Number(summary.reviewScoreAdjustment || 0) > 0 ? '+' : ''}{Number(summary.reviewScoreAdjustment || 0).toFixed(2)}</div>
                          {summary.calculatedAt && <div className="mt-1 whitespace-nowrap text-xs text-gray-400">Calculated {new Date(summary.calculatedAt).toLocaleString()}</div>}
                          <button
                            type="button"
                            onClick={() => loadSummary(item.propertyId)}
                            disabled={isCalculating}
                            className="mt-1 block text-xs font-medium text-sky-700 hover:text-sky-900 disabled:text-gray-400"
                          >
                            {isCalculating ? 'Refreshing…' : 'Refresh'}
                          </button>
                        </div>
                      ) : (
                        <div>
                          <button
                            type="button"
                            onClick={() => loadSummary(item.propertyId)}
                            disabled={isCalculating}
                            className="whitespace-nowrap rounded-md border border-sky-600 px-3 py-1.5 text-xs font-medium text-sky-700 hover:bg-sky-50 disabled:border-gray-300 disabled:text-gray-400"
                          >
                            {isCalculating ? 'Calculating…' : 'Calculate score'}
                          </button>
                          {summaryError && <div className="mt-1 max-w-40 text-xs text-red-600">{summaryError}</div>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {summary ? <>
                        <div>{summary.openCount} open · {summary.inProgressCount} in progress</div>
                        {summary.oldestOpenDays != null && <div className="mt-1 text-xs text-gray-500">Oldest: {summary.oldestOpenDays} days</div>}
                      </> : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-700">{summary ? summary.urgentCount : <span className="text-gray-400">—</span>}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{summary ? summary.staleCandidateCount : <span className="text-gray-400">—</span>}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{summary ? summary.completedCount : <span className="text-gray-400">—</span>}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {summary ? <>
                        <div>{summary.reviewCount}</div>
                        <div className="text-xs text-gray-500">{summary.classifiedReviewCount} analyzed · {summary.unclassifiedReviewCount} pending</div>
                        {summary.averageReviewPoints != null && <div className="text-xs text-gray-500">Avg raw points: {summary.averageReviewPoints.toFixed(1)}</div>}
                      </> : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Link
                        to={`/dashboard/reports/property-performance/${item.propertyId}`}
                        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700 transition-colors hover:border-sky-300 hover:bg-sky-100 hover:text-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                        aria-label={`View details for ${item.propertyName}`}
                      >
                        View Details
                        <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7 4.75 12.25 10 7 15.25" />
                        </svg>
                      </Link>
                    </td>
                  </tr>
                  );
                })}
                {!items.length && <tr><td colSpan="8" className="px-4 py-10 text-center text-gray-500">No matching properties found.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-gray-600">
              {response.total} {streamlineOnly ? 'Streamline-connected' : 'active'} {response.total === 1 ? 'property' : 'properties'}
            </div>
            {pages > 1 && <Pagination count={pages} page={page} onChange={(_, value) => setPage(value)} color="primary" />}
          </div>
        </div>
      )}
    </div>
  );
}
